import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronDown, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { provinces, provinceKommunMap } from "@/lib/provinceKommunData";
import {
  slugifyUsername, isUsernameAvailable, isValidUsernameFormat,
  USERNAME_MIN, USERNAME_MAX,
} from "@/lib/usernameUtils";

export default function CompleteProfile() {
  const { user, profile, signOut, refreshProfile, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as any)?.from || "/";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [province, setProvince] = useState("");
  const [kommun, setKommun] = useState("");
  const [provinceOpen, setProvinceOpen] = useState(false);
  const [kommunOpen, setKommunOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Prefill from existing profile (e.g. auto-generated username)
  useEffect(() => {
    if (profile) {
      if (profile.full_name) setName(profile.full_name);
      if (profile.phone) setPhone(profile.phone);
      if (profile.username) setUsername(profile.username);
      if (profile.province) setProvince(profile.province);
      if (profile.municipality) setKommun(profile.municipality);
    }
  }, [profile]);

  // Redirect already-complete users away
  useEffect(() => {
    if (user && isProfileComplete) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, isProfileComplete, navigate, redirectTo]);

  const kommuner = useMemo(() => (province ? provinceKommunMap[province] ?? [] : []), [province]);

  useEffect(() => {
    if (!username) { setUsernameStatus("idle"); return; }
    if (!isValidUsernameFormat(username)) { setUsernameStatus("invalid"); return; }
    if (profile?.username === username) { setUsernameStatus("available"); return; }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      const ok = await isUsernameAvailable(username);
      setUsernameStatus(ok ? "available" : "taken");
    }, 350);
    return () => clearTimeout(t);
  }, [username, profile?.username]);

  const handleBack = async () => {
    await signOut();
    navigate("/registrera", { replace: true });
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = "Det här fältet är obligatoriskt.";
    if (!phone.trim()) e.phone = "Det här fältet är obligatoriskt.";
    if (!username.trim()) e.username = "Det här fältet är obligatoriskt.";
    else if (!isValidUsernameFormat(username)) e.username = "Användarnamnet får bara innehålla små bokstäver, siffror och bindestreck.";
    else if (usernameStatus === "taken") e.username = "Det här användarnamnet är redan taget.";
    if (!province) e.province = "Det här fältet är obligatoriskt.";
    if (!kommun) e.kommun = "Det här fältet är obligatoriskt.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) { toast.error("Du måste godkänna villkoren"); return; }
    if (!validate()) return;
    if (!user) return;

    setLoading(true);
    // Re-confirm username
    if (profile?.username !== username) {
      const free = await isUsernameAvailable(username);
      if (!free) {
        setLoading(false);
        setUsernameStatus("taken");
        setErrors((prev) => ({ ...prev, username: "Det här användarnamnet är redan taget." }));
        return;
      }
    }

    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: name.trim(),
        phone: phone.trim(),
        username,
        province,
        municipality: kommun,
        location: `${kommun}, ${province}`,
        account_type: "personal",
      })
      .eq("id", user.id);

    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }
    await refreshProfile();
    setLoading(false);
    toast.success("Profilen är klar!");
    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <button
            onClick={handleBack}
            className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
            type="button"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Välj annan kontotyp
          </button>
          <h1 className="mb-2 text-center font-display text-2xl font-bold">Slutför din registrering</h1>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            Fyll i dina uppgifter för att komma igång med ditt Barka-konto.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Namn</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Förnamn Efternamn"
              />
              {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="phone">Telefonnummer</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="070-123 45 67"
              />
              {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
            </div>

            <div>
              <Label htmlFor="username">Användarnamn</Label>
              <div className="relative">
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(slugifyUsername(e.target.value))}
                  placeholder="erik-lindqvist"
                  minLength={USERNAME_MIN}
                  maxLength={USERNAME_MAX}
                  autoCapitalize="none"
                  autoComplete="off"
                  className={cn(
                    usernameStatus === "available" && "border-primary focus-visible:ring-primary/40",
                    (usernameStatus === "taken" || usernameStatus === "invalid") && "border-destructive focus-visible:ring-destructive/40",
                  )}
                />
                {usernameStatus === "available" && (
                  <Check className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Din profil-URL: timbernet.se/säljare/{username || "..."}
              </p>
              {usernameStatus === "taken" && (
                <p className="mt-1 text-xs text-destructive">Det här användarnamnet är redan taget.</p>
              )}
              {usernameStatus === "invalid" && username.length > 0 && (
                <p className="mt-1 text-xs text-destructive">Användarnamnet får bara innehålla små bokstäver, siffror och bindestreck.</p>
              )}
              {errors.username && usernameStatus !== "taken" && usernameStatus !== "invalid" && (
                <p className="mt-1 text-xs text-destructive">{errors.username}</p>
              )}
            </div>

            <div>
              <Label>Län</Label>
              <Popover open={provinceOpen} onOpenChange={setProvinceOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
                    {province || "Välj län"}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Sök län..." />
                    <CommandList>
                      <CommandEmpty>Inget län hittades.</CommandEmpty>
                      <CommandGroup>
                        {provinces.map((p) => (
                          <CommandItem key={p} value={p} onSelect={() => {
                            setProvince(p); setKommun(""); setProvinceOpen(false);
                          }}>
                            <Check className={cn("mr-2 h-4 w-4", province === p ? "opacity-100" : "opacity-0")} />
                            {p}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.province && <p className="mt-1 text-xs text-destructive">{errors.province}</p>}
            </div>

            {province && (
              <div>
                <Label>Kommun</Label>
                <Popover open={kommunOpen} onOpenChange={setKommunOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
                      {kommun || "Välj kommun"}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök kommun..." />
                      <CommandList>
                        <CommandEmpty>Ingen kommun hittades.</CommandEmpty>
                        <CommandGroup>
                          {kommuner.map((k) => (
                            <CommandItem key={k} value={k} onSelect={() => {
                              setKommun(k); setKommunOpen(false);
                            }}>
                              <Check className={cn("mr-2 h-4 w-4", kommun === k ? "opacity-100" : "opacity-0")} />
                              {k}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.kommun && <p className="mt-1 text-xs text-destructive">{errors.kommun}</p>}
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox id="terms" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} />
              <label htmlFor="terms" className="text-xs text-muted-foreground leading-snug">
                Jag godkänner <Link to="/villkor" className="underline">villkoren</Link> och <Link to="/integritetspolicy" className="underline">integritetspolicyn</Link>
              </label>
            </div>

            <Button
              className="w-full"
              type="submit"
              disabled={
                loading ||
                !agreed ||
                !name.trim() ||
                !phone.trim() ||
                !username.trim() ||
                !province ||
                !kommun ||
                usernameStatus === "taken" ||
                usernameStatus === "invalid" ||
                usernameStatus === "checking"
              }
            >
              {loading ? "Sparar…" : "Slutför registrering"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
